import { useState, useEffect } from 'react'
import Header from './components/Header'
import Footer from './components/Footer'
import './styles/App.css'
import {
  initializeFFmpeg,
  extractAudio,
  getVideoMetadata,
  generateThumbnail,
  downloadAudio,
  formatDuration,
  validateVideoFile,
  type ExtractOptions,
  type VideoMetadata,
} from './utils/videoExtract'

type AudioFormat = 'mp3' | 'wav' | 'aac' | 'ogg'
type Quality = 'low' | 'medium' | 'high'

function App() {
  const [file, setFile] = useState<File | null>(null)
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [metadata, setMetadata] = useState<VideoMetadata | null>(null)
  const [thumbnail, setThumbnail] = useState<string | null>(null)
  const [outputBlob, setOutputBlob] = useState<Blob | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [format, setFormat] = useState<AudioFormat>('mp3')
  const [quality, setQuality] = useState<Quality>('medium')

  // Manage audio URL lifecycle to prevent memory leaks
  useEffect(() => {
    if (outputBlob) {
      const url = URL.createObjectURL(outputBlob)
      setAudioUrl(url)
      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setAudioUrl(null)
    }
  }, [outputBlob])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    const validation = validateVideoFile(selectedFile)
    if (!validation.valid) {
      setError(validation.error || 'Invalid file')
      return
    }

    setFile(selectedFile)
    setError(null)
    setOutputBlob(null)

    try {
      const meta = await getVideoMetadata(selectedFile)
      setMetadata(meta)

      const thumb = await generateThumbnail(selectedFile, 1)
      setThumbnail(thumb)
    } catch (err) {
      console.error('Metadata error:', err)
    }
  }

  const handleExtract = async () => {
    if (!file || processing) return

    setProcessing(true)
    setProgress(0)
    setError(null)
    setOutputBlob(null)

    try {
      await initializeFFmpeg((prog, msg) => {
        setProgress(prog)
        setProgressMessage(msg)
      })
      const options: ExtractOptions = { format, quality }
      const result = await extractAudio(file, options, (prog, msg) => {
        setProgress(prog)
        setProgressMessage(msg)
      })

      setOutputBlob(result)
      setProgress(100)
      setProgressMessage('Complete!')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Extraction failed')
    } finally {
      setProcessing(false)
    }
  }

  const handleDownload = () => {
    if (!outputBlob || !file) return
    downloadAudio(outputBlob, file.name, format)
  }

  return (
    <div className="app">
      <Header
        title="Video Extract Audio"
        description="Extract audio tracks from video files - 100% client-side"
      />

      <main className="main-content">
        <div className="container">
          <div className="upload-section">
            <input
              type="file"
              accept=".mp4,.webm,.mov,.avi,.mkv,video/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="video-upload"
            />
            <label htmlFor="video-upload" className="upload-btn">
              📁 Select Video File
            </label>
            <p className="upload-hint">
              Supports: MP4, WebM, MOV, AVI, MKV (max 1GB)
            </p>
          </div>

          {error && <div className="error-message">❌ {error}</div>}

          {file && metadata && (
            <div className="video-info-section">
              {thumbnail && (
                <div className="thumbnail-container">
                  <img src={thumbnail} alt="Video thumbnail" className="thumbnail" />
                </div>
              )}

              <div className="video-details">
                <h3>{file.name}</h3>
                <div className="details-grid">
                  <div><strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB</div>
                  <div><strong>Duration:</strong> {formatDuration(metadata.duration)}</div>
                  <div><strong>Resolution:</strong> {metadata.width}x{metadata.height}</div>
                </div>
              </div>

              <div className="options-section">
                <div className="option-group">
                  <label>Audio Format:</label>
                  <select value={format} onChange={(e) => setFormat(e.target.value as AudioFormat)}>
                    <option value="mp3">MP3</option>
                    <option value="wav">WAV</option>
                    <option value="aac">AAC</option>
                    <option value="ogg">OGG/Vorbis</option>
                  </select>
                </div>

                <div className="option-group">
                  <label>Quality:</label>
                  <select value={quality} onChange={(e) => setQuality(e.target.value as Quality)}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <button
                className="btn-primary"
                onClick={handleExtract}
                disabled={processing}
              >
                {processing ? '⏳ Extracting...' : '🎵 Extract Audio'}
              </button>

              {processing && (
                <div className="progress-section">
                  <p>{progressMessage}</p>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                  </div>
                  <p className="progress-percent">{progress.toFixed(0)}%</p>
                </div>
              )}

              {outputBlob && !processing && audioUrl && (
                <div className="output-section">
                  <h3>✅ Audio Extracted Successfully!</h3>
                  <audio controls src={audioUrl} style={{ width: '100%', marginTop: '1rem' }} />
                  <button className="btn-primary" onClick={handleDownload} style={{ marginTop: '1rem' }}>
                    💾 Download Audio
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="privacy-notice">
            <h3>🔒 100% Privacy</h3>
            <p>All processing happens in your browser using FFmpeg WebAssembly. No files are uploaded to any server.</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default App
